import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  fetchLeaderboard,
  type LeaderboardResponse,
  type LeaderboardType,
} from "../api/leaderboard.api";
import { fetchMyLeague, type League, type LeagueStanding } from "../api/league.api";

/**
 * Ranking views: three lifetime leaderboards + a "my league" weekly tier
 * standings. All four are tabs at the top of this scene.
 */

type ViewKey = LeaderboardType | "my-league";

interface BoardConfig {
  key: ViewKey;
  label: string;
  icon: string;
}

const VIEWS: BoardConfig[] = [
  { key: "my-league",     label: "League",  icon: "🏆" },
  { key: "tokens-earned", label: "Earnings", icon: "💰" },
  { key: "wheel-spins",   label: "Spins",    icon: "🎰" },
  { key: "referrals",     label: "Friends",  icon: "🤝" },
];

const LEAGUE_META: Record<
  League,
  { name: string; emoji: string; color: string }
> = {
  BRONZE:   { name: "Bronze",   emoji: "🥉", color: "#a16a3a" },
  SILVER:   { name: "Silver",   emoji: "🥈", color: "#b8c5d6" },
  GOLD:     { name: "Gold",     emoji: "🥇", color: "#f0b90b" },
  PLATINUM: { name: "Platinum", emoji: "💎", color: "#67e8f9" },
  LEGEND:   { name: "Legend",   emoji: "👑", color: "#a855f7" },
};

function formatThousands(raw: string): string {
  return raw.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
}

export function LeaderboardScene() {
  const [active, setActive] = useState<ViewKey>("my-league");

  return (
    <div className="leaderboard-scene">
      <div className="leaderboard-tabs">
        {VIEWS.map((b) => (
          <button
            key={b.key}
            className={`leaderboard-tab ${b.key === active ? "active" : ""}`}
            onClick={() => setActive(b.key)}
            type="button"
          >
            <span aria-hidden>{b.icon}</span>
            <span>{b.label}</span>
          </button>
        ))}
      </div>

      {active === "my-league" ? (
        <MyLeagueView />
      ) : (
        <LifetimeBoardView type={active} />
      )}
    </div>
  );
}

// ─── Lifetime board (tokens / spins / referrals) ─────────────────────

const LIFETIME_UNIT: Record<LeaderboardType, string> = {
  "tokens-earned": "tokens",
  "wheel-spins":   "spins",
  "referrals":     "invited",
};

function LifetimeBoardView({ type }: { type: LeaderboardType }) {
  const [data, setData] = useState<LeaderboardResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    setData(null);
    fetchLeaderboard(type, 50)
      .then((resp) => {
        if (!cancelled) setData(resp);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        const msg =
          (err as { response?: { data?: { message?: string } } })?.response?.data?.message ??
          (err as Error).message;
        setError(msg ?? "Failed to load leaderboard");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [type]);

  const unit = LIFETIME_UNIT[type];

  return (
    <>
      {error && <div className="error-banner">{error}</div>}
      {loading && !data && <div className="loading">Loading leaderboard…</div>}

      {data && (
        <>
          <div className="leaderboard-header">
            <span className="muted">Lifetime ranking</span>
          </div>

          <ul className="leaderboard-list">
            <AnimatePresence mode="popLayout">
              {data.entries.map((e) => (
                <Row
                  key={e.userId}
                  rank={e.rank}
                  username={e.username}
                  value={e.value}
                  isMe={e.isMe}
                  unit={unit}
                />
              ))}
            </AnimatePresence>
            {data.entries.length === 0 && (
              <li className="empty">No players yet — be the first.</li>
            )}
          </ul>

          {data.me && !data.entries.some((e) => e.isMe) && (
            <div className="leaderboard-me">
              <div className="muted">Your rank</div>
              <ul className="leaderboard-list">
                <Row
                  rank={data.me.rank}
                  username={data.me.username}
                  value={data.me.value}
                  isMe
                  unit={unit}
                />
              </ul>
            </div>
          )}
        </>
      )}
    </>
  );
}

// ─── My League view ──────────────────────────────────────────────────

function MyLeagueView() {
  const [data, setData] = useState<LeagueStanding | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [now, setNow] = useState(Date.now());

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    fetchMyLeague()
      .then((resp) => {
        if (!cancelled) setData(resp);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        const msg =
          (err as { response?: { data?: { message?: string } } })?.response?.data?.message ??
          (err as Error).message;
        setError(msg ?? "Failed to load league");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Tick the countdown.
  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, []);

  if (loading && !data) return <div className="loading">Loading league…</div>;
  if (error) return <div className="error-banner">{error}</div>;
  if (!data) return null;

  const meta = LEAGUE_META[data.myLeague];
  return (
    <>
      <LeagueBadge
        data={data}
        meta={meta}
        fetchedMsRemaining={data.msUntilNextWeek}
        now={now}
      />

      <div className="leaderboard-header">
        <span className="muted">This week's standings in</span>
        <strong style={{ color: meta.color }}>{meta.name}</strong>
        <span className="muted"> · {data.cohortSize} players</span>
      </div>

      <ul className="leaderboard-list">
        <AnimatePresence mode="popLayout">
          {data.standings.map((s) => (
            <Row
              key={s.userId}
              rank={s.rank}
              username={s.username}
              value={s.score}
              isMe={s.isMe}
              unit="tokens"
              promote={s.rank <= cutoffSize(data.cohortSize)}
              demote={s.rank > data.cohortSize - cutoffSize(data.cohortSize)}
            />
          ))}
        </AnimatePresence>
        {data.standings.length === 0 && (
          <li className="empty">
            No-one has scored this week yet. Earn tokens to take the lead.
          </li>
        )}
      </ul>

      {data.myRank !== null && !data.standings.some((s) => s.isMe) && (
        <div className="leaderboard-me">
          <div className="muted">Your rank</div>
          <ul className="leaderboard-list">
            <Row
              rank={data.myRank}
              username={null}
              value={data.myScore}
              isMe
              unit="tokens"
            />
          </ul>
        </div>
      )}
    </>
  );
}

function cutoffSize(cohortSize: number): number {
  return Math.max(1, Math.min(3, Math.floor(cohortSize / 3)));
}

interface BadgeProps {
  data: LeagueStanding;
  meta: { name: string; emoji: string; color: string };
  fetchedMsRemaining: number;
  now: number;
}

function LeagueBadge({ data, meta, fetchedMsRemaining, now }: BadgeProps) {
  // Pin the absolute deadline once, so ticks don't lie if the user lingers.
  const [deadlineMs] = useState(() => now + fetchedMsRemaining);
  const remaining = Math.max(0, deadlineMs - now);
  const days = Math.floor(remaining / (24 * 60 * 60 * 1000));
  const hours = Math.floor((remaining % (24 * 60 * 60 * 1000)) / (60 * 60 * 1000));
  const mins = Math.floor((remaining % (60 * 60 * 1000)) / (60 * 1000));
  const secs = Math.floor((remaining % (60 * 1000)) / 1000);

  return (
    <div className="league-badge" style={{ borderColor: meta.color }}>
      <div className="league-emoji" aria-hidden>
        {meta.emoji}
      </div>
      <div className="league-text">
        <div className="league-name" style={{ color: meta.color }}>
          {meta.name} League
        </div>
        <div className="league-stats">
          <span>
            <strong>#{data.myRank ?? "—"}</strong>{" "}
            <span className="muted">of {data.cohortSize}</span>
          </span>
          <span>
            <strong>{formatThousands(data.myScore)}</strong>{" "}
            <span className="muted">tokens this week</span>
          </span>
        </div>
      </div>
      <div className="league-countdown" title="Time until weekly reset">
        <span className="muted countdown-label">Resets in</span>
        <span className="countdown-value">
          {days > 0 ? `${days}d ` : ""}
          {String(hours).padStart(2, "0")}:{String(mins).padStart(2, "0")}:
          {String(secs).padStart(2, "0")}
        </span>
      </div>
    </div>
  );
}

// ─── Shared row ──────────────────────────────────────────────────────

function Row({
  rank,
  username,
  value,
  isMe,
  unit,
  promote = false,
  demote = false,
}: {
  rank: number;
  username: string | null;
  value: string;
  isMe: boolean;
  unit: string;
  promote?: boolean;
  demote?: boolean;
}) {
  const medal =
    rank === 1 ? "🥇" : rank === 2 ? "🥈" : rank === 3 ? "🥉" : null;
  return (
    <motion.li
      className={`leaderboard-row ${isMe ? "me" : ""} ${
        promote ? "promote" : ""
      } ${demote ? "demote" : ""}`}
      layout
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.18 }}
    >
      <span className="rank">
        {medal ? <span className="medal">{medal}</span> : <span>#{rank}</span>}
      </span>
      <span className="name">
        {username ?? "anon"}
        {promote && <span className="movement up">↑</span>}
        {demote && <span className="movement down">↓</span>}
      </span>
      <span className="value">
        <strong>{formatThousands(value)}</strong>
        <span className="muted unit"> {unit}</span>
      </span>
    </motion.li>
  );
}
